"use client";

import { AlertCircle, Check, Copy } from "lucide-react";
import { type FormEvent, useState } from "react";
import { AutoInputForm } from "@/components/citation-tool/auto-input-form";
import { ManualEntryForm } from "@/components/citation-tool/manual-entry-form";
import { useToolUi } from "@/components/tools/tool-ui";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { trackCitationCopied, trackCitationGenerated } from "@/lib/analytics";
import { writeToClipboard } from "@/lib/clipboard";

/**
 * Moved here verbatim from `tools/chicago-footnote-generator/client.tsx`. The one change
 * is that its fifteen inline strings became `CHICAGO_FOOTNOTE_STRINGS`, so a translated
 * content file can override them through `ui`; the English values are byte-identical.
 *
 * The two input forms it delegates to read their own labels from `ToolStringsProvider`,
 * which is the generator's existing dictionary-backed context — so this widget only owns
 * the strings that are genuinely its own.
 */

// Chicago notes-and-bibliography is the CSL style whose <citation> layout emits the full
// footnote. Fixed for this tool (issue #93).
const STYLE = "chicago-notes-bibliography";

/** English strings. A translated content file overrides these through `ui`. */
export const CHICAGO_FOOTNOTE_STRINGS = {
  autoTab: "Paste URL / DOI / ISBN",
  manualTab: "Enter manually",
  copy: "Copy",
  copied: "Copied",
  copyFailed:
    "Couldn't copy automatically — select the text and copy it manually.",
  footnoteTitle: "Footnote",
  footnoteDescription:
    "Drop this into your text as a numbered footnote or endnote.",
  bibliographyTitle: "Bibliography Entry",
  bibliographyDescription: "Add this to your bibliography (works-cited list).",
  emptyInput: "Enter a URL, DOI, or ISBN.",
  titleRequired: "At least a title is required.",
  rateLimited: "Rate limited. Please wait a minute and try again.",
  failed: "Could not generate the footnote.",
  networkError: "Network error. Please try again.",
};

type Strings = typeof CHICAGO_FOOTNOTE_STRINGS;

// Coarse classification of a pasted identifier, only for the analytics input_type signal.
const DOI_REGEX = /^10\.\d/;
const ISBN_REGEX = /^97[89]\d{10}$|^97[89]-/;

function detectAutoType(input: string): "doi" | "isbn" | "url" {
  if (DOI_REGEX.test(input)) {
    return "doi";
  }
  if (ISBN_REGEX.test(input)) {
    return "isbn";
  }
  return "url";
}

interface EditableEntry {
  title?: string;
  author?: string;
  issued?: string;
  URL?: string;
  publisher?: string;
  containerTitle?: string;
  type?: string;
  volume?: string;
  issue?: string;
  page?: string;
  DOI?: string;
  ISBN?: string;
}

type InputTab = "auto" | "manual";

interface CiteRequest {
  input?: string;
  entry?: EditableEntry;
}

function OutputCard({
  title,
  description,
  text,
  strings,
}: {
  title: string;
  description: string;
  text: string;
  strings: Strings;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  async function handleCopy() {
    const ok = await writeToClipboard(text);
    if (ok) {
      setCopied(true);
      setCopyFailed(false);
      trackCitationCopied(STYLE);
      setTimeout(() => setCopied(false), 2000);
      return;
    }
    setCopyFailed(true);
  }

  return (
    <Card className="animate-fade-in">
      <CardHeader className="flex flex-col items-stretch gap-3 space-y-0 pb-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button
          className="w-full shrink-0 sm:w-auto"
          onClick={handleCopy}
          size="sm"
          type="button"
          variant="outline"
        >
          {copied ? (
            <>
              <Check className="size-4" />
              {strings.copied}
            </>
          ) : (
            <>
              <Copy className="size-4" />
              {strings.copy}
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent>
        <p className="break-words text-foreground text-sm leading-relaxed">
          {text}
        </p>
        {copyFailed && (
          <p className="mt-2 text-destructive text-xs">{strings.copyFailed}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function ChicagoFootnoteGeneratorWidget() {
  const strings = useToolUi(CHICAGO_FOOTNOTE_STRINGS);
  const [tab, setTab] = useState<InputTab>("auto");
  const [autoInput, setAutoInput] = useState("");
  const [manualEntry, setManualEntry] = useState<EditableEntry>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [bibliography, setBibliography] = useState<string | null>(null);

  async function generate(
    payload: CiteRequest,
    inputType: "doi" | "isbn" | "url" | "manual"
  ) {
    setError(null);
    setLoading(true);
    setNote(null);
    setBibliography(null);

    try {
      const res = await fetch("/api/cite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, style: STYLE, note: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(
          data.error ??
            (res.status === 429 ? strings.rateLimited : strings.failed)
        );
        return;
      }
      setNote(data.note ?? "");
      setBibliography(data.citation ?? "");
      trackCitationGenerated(STYLE, inputType);
    } catch {
      setError(strings.networkError);
    } finally {
      setLoading(false);
    }
  }

  async function handleAutoSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = autoInput.trim();
    if (!trimmed) {
      setError(strings.emptyInput);
      return;
    }
    await generate({ input: trimmed }, detectAutoType(trimmed));
  }

  async function handleManualSubmit(e: FormEvent) {
    e.preventDefault();
    if (!manualEntry.title?.trim()) {
      setError(strings.titleRequired);
      return;
    }
    await generate({ entry: manualEntry }, "manual");
  }

  return (
    <div className="space-y-6">
      {/* Input mode toggle */}
      <div
        className="inline-flex rounded-lg border border-border bg-muted/40 p-1"
        role="tablist"
      >
        <button
          aria-selected={tab === "auto"}
          className={`rounded-md px-4 py-1.5 font-medium text-sm transition-colors ${
            tab === "auto"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setTab("auto")}
          role="tab"
          type="button"
        >
          {strings.autoTab}
        </button>
        <button
          aria-selected={tab === "manual"}
          className={`rounded-md px-4 py-1.5 font-medium text-sm transition-colors ${
            tab === "manual"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setTab("manual")}
          role="tab"
          type="button"
        >
          {strings.manualTab}
        </button>
      </div>

      {tab === "auto" ? (
        <AutoInputForm
          loading={loading}
          onChange={setAutoInput}
          onSubmit={handleAutoSubmit}
          value={autoInput}
        />
      ) : (
        <ManualEntryForm
          entry={manualEntry}
          loading={loading}
          onChange={setManualEntry}
          onSubmit={handleManualSubmit}
        />
      )}

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-destructive text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          {error}
        </div>
      )}

      {note && (
        <OutputCard
          description={strings.footnoteDescription}
          strings={strings}
          text={note}
          title={strings.footnoteTitle}
        />
      )}

      {bibliography && (
        <OutputCard
          description={strings.bibliographyDescription}
          strings={strings}
          text={bibliography}
          title={strings.bibliographyTitle}
        />
      )}
    </div>
  );
}
